import { useMasterData } from "@/stores/master-data";
import { FunctionalGroupCode } from "@/types/DocumentTypes";

/**
 * Returns selected additional service code(s) for a given additional service group
 * uses masterdata.additionalServices to find the group
 *
 * Note: This function is used by the curation store and revision flow which still work
 * with the legacy `additional_services` array. The raw flow has been migrated to typed fields
 * (`duration`, `features`, `product_brand`, `promotional_option`, `own_offer_classification`,
 * `nat_geo_tour_level`, `animal_welfare_tags`, `health_and_safety_tags`, `other_tags`).
 *
 * @param code - name of additional service group
 * @param additionalServiceCodes - existing additional service codes usually coming from backend functional.additional_services
 * @param multi - return one or multiple additional codes (depends on type of the group)
 *
 * @example
 * const additionalServices = ["2_4_HOURS", "ANOTHER_CODE", "BRAND_SCENE"]
 * - multi:
 * getSelectedAdditionalServices("DURATION", additionalServices, true) //=> ["2_4_HOURS", "ANOTHER_CODE"]
 * - single:
 * getSelectedAdditionalServices("PRODUCT_BRAND", additionalServices, false) //=> "BRAND_SCENE"
 */
export function getSelectedAdditionalServices<M extends boolean>(
  code: FunctionalGroupCode,
  additionalServiceCodes: string[],
  multi: M
): M extends true ? string[] : string {
  const masterData = useMasterData();
  const options = masterData.getAdditionalServicesByFGCode(code);
  const selectedOptions = options
    .filter((option) => additionalServiceCodes.includes(option.code))
    .map((option) => option.code);

  /* eslint-disable @typescript-eslint/no-explicit-any */
  if (multi) {
    return selectedOptions as any;
  } else if (selectedOptions[0]) {
    return selectedOptions[0] as any;
  } else {
    return "" as any;
  }
  /* eslint-enable @typescript-eslint/no-explicit-any */
}
